import React from "react";
import { colors } from '../../../../theme/colors';
import {
  Avatar,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  MenuItem,
  FormControl,
  Select,
  Button,
  Tooltip,
  Zoom,
  Skeleton,
  Chip,
  TextField,
  InputAdornment,
} from "@mui/material";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import TableRowsOutlinedIcon from "@mui/icons-material/TableRowsOutlined";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import AddIcon from "@mui/icons-material/Add";
import { MainWrapper } from "./style";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CustomConfirmModel from "../../../../components/common-components/CustomConfirmModel";
import useRecruiterList from "./useRecruiterList";

const getRoleChipProps = (roleStr) => {
  const role = (roleStr || "").toLowerCase();
  if (role.includes("admin")) {
    return {
      bg: colors.primaryLight,
      color: colors.primary,
      border: colors.primaryLightBorder,
    };
  }
  if (role.includes("recruiter")) {
    return {
      bg: "#ecfdf5",
      color: "#059669",
      border: "#a7f3d0",
    };
  }
  if (role.includes("manager")) {
    return {
      bg: "#fffbe1",
      color: "#d97706",
      border: "#fde68a",
    };
  }
  return {
    bg: "#f1f5f9",
    color: "#475569",
    border: "#cbd5e1",
  };
};

const RecruiterList = () => {
  const {
    filterName,
    filterOptions,
    open,
    selectedRecruiter,
    selectedRecruiterName,
    users,
    headCells,
    handleFilterChange,
    handleRowsPerPageChange,
    handlePrevPage,
    handleNextPage,
    handleClickOpen,
    handleClose,
    handleDelete,
    handleEditRecruiter,
    handleAddRecruiter,
    handleResetFilters,
    loading,
    deleteLoading,
  } = useRecruiterList();

  const activeFilterCount = filterName ? 1 : 0;

  const SkeletonRow = () => (
    <TableRow>
      <TableCell>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Skeleton variant="circular" width={34} height={34} />
          <Skeleton variant="text" width={100} height={24} />
        </Box>
      </TableCell>
      <TableCell>
        <Skeleton variant="text" width={160} height={24} />
      </TableCell>
      <TableCell>
        <Skeleton variant="text" width={110} height={24} />
      </TableCell>
      <TableCell>
        <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: "6px" }} />
      </TableCell>
      <TableCell>
        <Skeleton variant="text" width={120} height={24} />
      </TableCell>
      <TableCell>
        <Box className="action-buttons-container">
          <Skeleton variant="circular" width={30} height={30} />
          <Skeleton variant="circular" width={30} height={30} />
        </Box>
      </TableCell>
    </TableRow>
  );

  return (
    <>
      <MainWrapper>
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            mb: 3,
            borderRadius: "16px",
            backgroundColor: "#ffffff",
            border: "1px solid #e2e8f0",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.03)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              pb: 2,
              mb: 2,
              borderBottom: "1px solid #f1f5f9",
            }}
          >
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: "#1e293b",
                    letterSpacing: "-0.5px",
                    fontSize: "1.35rem",
                  }}
                >
                  Recruiter List
                </Typography>
                <Chip
                  label={`${users.data?.total ?? users.data?.totalUsers ?? users.data?.totalCount ?? users.data?.count ?? users.data?.data?.length ?? 0} Recruiters`}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.75rem",
                    backgroundColor: colors.primaryLight,
                    color: colors.primary,
                    borderRadius: "6px",
                  }}
                />
              </Box>
              <Typography variant="body2" sx={{ color: "#64748b", mt: 0.5 }}>
                Manage and configure recruiters in your organization.
              </Typography>
            </Box>

            <Button
              className="btn"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddRecruiter}
              sx={{
                height: "40px",
                px: 2.5,
                fontWeight: 600,
                fontSize: "0.85rem",
                borderRadius: "10px",
                whiteSpace: "nowrap",
              }}
            >
              Add Recruiter
            </Button>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 1.5,
                flex: 1,
                minWidth: { xs: "100%", md: "auto" },
              }}
            >
              <TextField
                size="small"
                placeholder="Search by Recruiter name..."
                value={filterName}
                onChange={handleFilterChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "#64748b", fontSize: "1.1rem" }} />
                    </InputAdornment>
                  ),
                  endAdornment: filterName ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => handleFilterChange({ target: { value: "" } })}
                        edge="end"
                      >
                        <ClearIcon sx={{ fontSize: "0.95rem" }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
                sx={{
                  minWidth: { xs: "100%", sm: "280px" },
                  flex: { sm: 1, md: "0 1 340px" },
                  "& .MuiOutlinedInput-root": {
                    height: "38px",
                    borderRadius: "10px",
                    fontSize: "0.85rem",
                    backgroundColor: filterName ? colors.primaryLight : "#ffffff",
                    "& fieldset": {
                      borderColor: filterName ? colors.primaryLightBorder : "#cbd5e1",
                    },
                    "&:hover fieldset": {
                      borderColor: colors.primary,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: colors.primary,
                      borderWidth: "1.5px",
                    },
                  },
                }}
              />

              <FormControl size="small" sx={{ minWidth: "140px" }}>
                <Select
                  value={filterOptions.limit}
                  onChange={handleRowsPerPageChange}
                  displayEmpty
                  startAdornment={
                    <InputAdornment position="start">
                      <TableRowsOutlinedIcon sx={{ color: "#64748b", fontSize: "1.05rem" }} />
                    </InputAdornment>
                  }
                  sx={{
                    height: "38px",
                    borderRadius: "10px",
                    fontSize: "0.85rem",
                    fontWeight: 500,
                    backgroundColor: "#ffffff",
                    "& .MuiSelect-select": { py: "6px", pl: 0.5 },
                  }}
                >
                  <MenuItem value={5}>5 per page</MenuItem>
                  <MenuItem value={10}>10 per page</MenuItem>
                  <MenuItem value={20}>20 per page</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {activeFilterCount > 0 && (
              <Button
                size="small"
                onClick={handleResetFilters}
                startIcon={<RotateLeftIcon sx={{ fontSize: "1rem" }} />}
                sx={{
                  textTransform: "none",
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  color: colors.primary,
                  backgroundColor: colors.primaryLight,
                  border: `1px solid ${colors.primaryLightBorder}`,
                  borderRadius: "8px",
                  px: 1.5,
                  py: "6px",
                  height: "38px",
                  whiteSpace: "nowrap",
                  "&:hover": {
                    backgroundColor: colors.sidebarSelected || colors.primaryLight,
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                  },
                }}
              >
                Clear Filter
              </Button>
            )}
          </Box>
        </Paper>

        <TableContainer className="custom-scrollbar table-container">
          <Table className="main-table" stickyHeader>
            <TableHead>
              <TableRow>
                {headCells.map((headCell, index) => (
                  <TableCell key={index}>{headCell.label}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: filterOptions.limit || 5 }).map(
                  (_, index) => <SkeletonRow key={`skeleton-${index}`} />
                )
              ) : users?.data?.data?.length > 0 ? (
                users.data?.data?.map((data) => {
                  const chipProps = getRoleChipProps(data?.role);
                  return (
                    <TableRow key={data?._id || data?.email}>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          <Avatar
                            sx={{
                              width: 34,
                              height: 34,
                              fontSize: "0.875rem",
                              fontWeight: 700,
                              backgroundColor: colors.primary,
                              color: "#ffffff",
                            }}
                          >
                            {data?.name ? data.name.charAt(0).toUpperCase() : "R"}
                          </Avatar>
                          <Typography variant="body2" fontWeight={600} color="#1e293b">
                            {data?.name || "N/A"}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{data?.email}</TableCell>
                      <TableCell>{data?.phone || "N/A"}</TableCell>
                      <TableCell>
                        <Chip
                          label={data?.role || "Recruiter"}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.75rem",
                            textTransform: "capitalize",
                            backgroundColor: chipProps.bg,
                            color: chipProps.color,
                            border: `1px solid ${chipProps.border}`,
                            borderRadius: "6px",
                          }}
                        />
                      </TableCell>
                      <TableCell>{data?.title || "N/A"}</TableCell>
                      <TableCell>
                        <Box className="action-buttons-container">
                          <Tooltip
                            title="Edit"
                            placement="top"
                            slots={{
                              transition: Zoom,
                            }}
                            arrow
                            PopperProps={{
                              modifiers: [
                                {
                                  name: "preventOverflow",
                                  options: {
                                    boundary: "window",
                                  },
                                },
                              ],
                            }}
                            componentsProps={{
                              tooltip: {
                                sx: {
                                  backgroundColor: colors.primary,
                                  color: "#fff",
                                  fontSize: "0.875rem",
                                },
                              },
                              arrow: {
                                sx: {
                                  color: colors.primary,
                                },
                              },
                            }}
                          >
                            <IconButton
                              variant="outlined"
                              onClick={() => handleEditRecruiter(data._id)}
                              size="small"
                              className="edit-button"
                              disabled={deleteLoading || loading}
                            >
                              <EditIcon className="icon-large" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip
                            title="Delete"
                            placement="top"
                            slots={{
                              transition: Zoom,
                            }}
                            arrow
                            PopperProps={{
                              modifiers: [
                                {
                                  name: "preventOverflow",
                                  options: {
                                    boundary: "window",
                                  },
                                },
                              ],
                            }}
                            componentsProps={{
                              tooltip: {
                                sx: {
                                  backgroundColor: colors.primary,
                                  color: "#fff",
                                  fontSize: "0.875rem",
                                },
                              },
                              arrow: {
                                sx: {
                                  color: colors.primary,
                                },
                              },
                            }}
                          >
                            <IconButton
                              onClick={() =>
                                handleClickOpen(data?._id, data?.name)
                              }
                              size="small"
                              variant="outlined"
                              className="delete-button"
                              disabled={deleteLoading || loading}
                            >
                              <DeleteIcon className="icon-large" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
                      <PeopleAltIcon sx={{ fontSize: 52, color: "#cbd5e1" }} />
                      <Typography variant="h6" fontWeight={600} color="text.secondary">
                        No recruiters found
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 360, textAlign: "center" }}>
                        {filterName
                          ? "No recruiter names match your search filter. Try clearing the search."
                          : "No recruiters have been configured yet. Click 'Add Recruiter' above to create one."}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box className="pagination-container">
          {loading ? (
            <>
              <Skeleton variant="rectangular" width={100} height={36} sx={{ borderRadius: "8px" }} />
              <Skeleton variant="text" width={60} height={28} />
              <Skeleton variant="rectangular" width={100} height={36} sx={{ borderRadius: "8px" }} />
            </>
          ) : (
            <>
              <Button
                className="btn"
                variant="contained"
                onClick={handlePrevPage}
                disabled={filterOptions.page === 1 || deleteLoading}
                startIcon={<NavigateBeforeIcon />}
              >
                Previous
              </Button>
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                Page {filterOptions.page}
              </Typography>
              <Button
                className="btn"
                variant="contained"
                onClick={handleNextPage}
                disabled={
                  !users?.data?.data ||
                  users.data?.data?.length < filterOptions.limit ||
                  deleteLoading
                }
                endIcon={<NavigateNextIcon />}
              >
                Next
              </Button>
            </>
          )}
        </Box>
      </MainWrapper>

      <CustomConfirmModel
        title="Confirm Delete"
        textContent="Are you sure you want to delete recruiter"
        confirmButton="Delete"
        open={open}
        handleClose={handleClose}
        id={selectedRecruiter}
        name={selectedRecruiterName ? `"${selectedRecruiterName}"? This action cannot be undone.` : ""}
        handleAction={handleDelete}
        loadingButtonType="Deleting"
        loading={deleteLoading}
      />
    </>
  );
};

export default RecruiterList;

